import React from 'react';
import { motion } from 'framer-motion';
import {
  Container,
  Typography,
  Grid,
  Box,
  Button,
  Divider,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  Chip
} from '@mui/material';
import {
  Public,
  LocationOn,
  Dataset,
  GpsFixed,
  Verified,
  Warning,
  Feedback,
  Timeline,
  CheckCircle,
  Error,
  Update,
  Map,
  LocationCity,
  Mail,
  Home
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import HomeRefreshButton from '../buttons/HomeRefreshButton';

const Methodology = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const methodologySteps = [
    {
      icon: <Dataset fontSize="large" />,
      title: 'Източник на данни',
      description: 'Данните са извлечени от Публичния регистър на народните читалища и на читалищните сдружения, поддържан от Министерството на културата.',
      details: [
        'Информация за дейността на читалищата',
        'Данни за адреси и местоположение',
        'Пълна правна и регистрационна информация'
      ],
      color: '#1976d2'
    },
    {
      icon: <GpsFixed fontSize="large" />,
      title: 'Обработка на адреси',
      description: 'След извличането им адресите са обработени с помощта на ГИС инструменти и визуализирани върху картата.',
      details: [
        'Геокодиране на адреси',
        'ГИС обработка',
        'Визуализация в географска проекция'
      ],
      color: '#2e7d32'
    },
    {
      icon: <LocationCity fontSize="large" />,
      title: 'Локация на читалища',
      description: 'В случаите, в които в регистъра не е посочен точен адрес, локацията е отбелязана в центъра на съответното село или град.',
      details: [
        'Пощенски код като основна точка',
        'Център на населено място',
        'Географски координати'
      ],
      color: '#ed6c02'
    },
    {
      icon: <Verified fontSize="large" />,
      title: 'Верификация на данни',
      description: 'Местоположението на читалищата е проверявано и ръчно, но този процес продължава и е възможно да има неточности.',
      details: [
        'Ръчна проверка на локации',
        'Постоянна актуализация',
        'Валидация на информацията'
      ],
      color: '#9c27b0'
    }
  ];

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        {/* Заглавие */}
        <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 6 } }}>
          <Timeline sx={{ fontSize: { xs: 48, md: 60 }, color: '#1976d2', mb: 2 }} />
          <Typography 
            variant={isMobile ? "h4" : "h3"} 
            component="h1" 
            gutterBottom
            sx={{ 
              fontWeight: 600,
              color: '#1a237e',
              mb: 3,
              textAlign: 'center'
            }}
          >
            Методология
          </Typography>
          <Typography 
            variant={isMobile ? "body1" : "h6"} 
            sx={{ 
              color: '#455a64',
              maxWidth: '800px',
              margin: '0 auto',
              mb: 4,
              textAlign: { xs: 'left', md: 'center' },
              lineHeight: 1.6
            }}
          >
            Процес на събиране, обработка и визуализация на данните за народните читалища
          </Typography>
          <Divider sx={{ 
            my: 4, 
            mx: 'auto', 
            width: { xs: '60px', md: '100px' }, 
            borderWidth: 2, 
            borderColor: '#1976d2' 
          }} />
        </Box>

        {/* Основна информация */}
        <Card elevation={2} sx={{ 
          mb: { xs: 4, md: 6 }, 
          p: { xs: 2, sm: 3, md: 5 }, 
          borderRadius: 3,
          bgcolor: 'background.paper'
        }}>
          <CardContent>
            <Typography 
              variant="body1" 
              paragraph
              sx={{ 
                fontSize: { xs: '1rem', md: '1.1rem' },
                lineHeight: 1.8,
                color: 'text.primary',
                mb: 4,
                textAlign: 'justify',
                hyphens: 'auto'
              }}
            >
              Данните, използвани за създаването на картата, са извлечени от <strong>Публичния регистър на народните читалища и на читалищните сдружения</strong>, поддържан от Министерството на културата. Те включват както информация за дейността на читалищата, така и данни за техните адреси и местоположение.
            </Typography>

            <Typography 
              variant="body1" 
              paragraph
              sx={{ 
                fontSize: { xs: '1rem', md: '1.1rem' },
                lineHeight: 1.8,
                color: 'text.primary',
                mb: 4,
                textAlign: 'justify',
                hyphens: 'auto'
              }}
            >
              След извличането им адресите са обработени с помощта на <strong>ГИС инструменти</strong> и визуализирани върху картата. Този процес включва автоматизирано геокодиране, ръчна валидация и оптимизация за визуализация в уеб среда.
            </Typography>

            <Box sx={{ 
              display: 'flex', 
              alignItems: 'flex-start', 
              mb: 4,
              p: { xs: 2, md: 3 },
              bgcolor: 'grey.50',
              borderRadius: 2
            }}>
              <LocationOn sx={{ 
                color: '#ed6c02', 
                mr: 2, 
                mt: 0.5, 
                fontSize: { xs: 24, md: 28 },
                flexShrink: 0
              }} />
              <Typography 
                variant="body1" 
                sx={{ 
                  fontSize: { xs: '1rem', md: '1.1rem' },
                  lineHeight: 1.8,
                  color: 'text.primary',
                  textAlign: 'justify',
                  hyphens: 'auto'
                }}
              >
                В случаите, в които в регистъра не е посочен точен адрес на читалището (например е наличен само пощенски код на населено място), локацията е отбелязана в центъра на съответното село или град. Този подход гарантира, че всички читалища са видими на картата, дори когато точният адрес не е наличен.
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {/* Степове на методологията */}
        <Typography 
          variant={isMobile ? "h5" : "h4"} 
          component="h2"
          sx={{ 
            fontWeight: 600,
            color: '#1a237e',
            mb: 4,
            textAlign: 'center'
          }}
        >
          Процес на обработка на данните
        </Typography>

        <Grid container spacing={3} sx={{ mb: { xs: 4, md: 6 } }}>
          {methodologySteps.map((step, index) => (
            <Grid item xs={12} md={6} key={index}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -5 }}
              >
                <Card 
                  elevation={2}
                  sx={{ 
                    height: '100%',
                    borderRadius: 3,
                    borderLeft: `6px solid ${step.color}`,
                    transition: 'all 0.3s ease',
                    bgcolor: 'background.paper',
                    '&:hover': {
                      boxShadow: 6,
                      transform: 'translateY(-5px)'
                    }
                  }}
                >
                  <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      mb: 3,
                      flexDirection: { xs: 'column', sm: 'row' },
                      textAlign: { xs: 'center', sm: 'left' }
                    }}>
                      <Box sx={{ 
                        color: step.color,
                        mr: { xs: 0, sm: 2 },
                        mb: { xs: 2, sm: 0 },
                        display: 'flex',
                        alignItems: 'center'
                      }}>
                        {React.cloneElement(step.icon, {
                          fontSize: isMobile ? 'medium' : 'large'
                        })}
                      </Box>
                      <Typography 
                        variant={isMobile ? "h6" : "h5"} 
                        component="h3"
                        sx={{ 
                          fontWeight: 600,
                          color: step.color
                        }}
                      >
                        {step.title}
                      </Typography>
                    </Box>
                    
                    <Typography 
                      variant="body1" 
                      paragraph
                      sx={{ 
                        color: 'text.primary',
                        mb: 3,
                        textAlign: 'justify',
                        hyphens: 'auto'
                      }}
                    >
                      {step.description}
                    </Typography>

                    <List dense>
                      {step.details.map((detail, idx) => (
                        <ListItem key={idx} sx={{ 
                          px: 0, 
                          py: 0.5,
                          alignItems: 'flex-start'
                        }}>
                          <ListItemIcon sx={{ minWidth: 36, mt: 0.5 }}>
                            <CheckCircle sx={{ 
                              color: step.color, 
                              fontSize: isMobile ? 16 : 20 
                            }} />
                          </ListItemIcon>
                          <ListItemText 
                            primary={detail}
                            primaryTypographyProps={{
                              variant: 'body2',
                              color: 'text.secondary',
                              textAlign: 'left'
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>

        {/* Важно съобщение за версията */}
        <Alert 
          severity="info"
          icon={<Update />}
          sx={{ 
            mb: { xs: 4, md: 6 },
            borderRadius: 3,
            '& .MuiAlert-icon': { 
              fontSize: { xs: 24, md: 30 },
              alignItems: 'center'
            }
          }}
        >
          <Box>
            <Typography variant={isMobile ? "subtitle1" : "h6"} gutterBottom>
              Версия 1.0
            </Typography>
            <Typography variant="body1" sx={{ textAlign: 'justify', hyphens: 'auto' }}>
              Картата е версия 1.0 и ще бъде постепенно актуализирана с корекции, допълнения и изчистване на грешки. Работим активно за подобряване на точността и пълнотата на данните.
            </Typography>
          </Box>
        </Alert>

        {/* Информация за точност на данните */}
        <Card 
          elevation={2}
          sx={{ 
            mb: { xs: 4, md: 6 },
            borderRadius: 3,
            border: '2px solid #ff9800',
            background: 'linear-gradient(135deg, #fff8e1 0%, #ffecb3 100%)'
          }}
        >
          <CardContent sx={{ p: { xs: 3, md: 4 } }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'flex-start', 
              mb: 3,
              flexDirection: { xs: 'column', sm: 'row' }
            }}>
              <Warning sx={{ 
                color: '#ff9800', 
                mr: { xs: 0, sm: 2 }, 
                mb: { xs: 2, sm: 0 },
                mt: { xs: 0, sm: 0.5 }, 
                fontSize: { xs: 28, md: 32 },
                flexShrink: 0
              }} />
              <Box sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
                <Typography 
                  variant={isMobile ? "h6" : "h5"} 
                  component="h3"
                  sx={{ 
                    fontWeight: 600,
                    color: '#e65100',
                    mb: 2
                  }}
                >
                  Внимание относно точността на данните
                </Typography>
                <Typography 
                  variant="body1"
                  sx={{ 
                    color: '#5d4037',
                    mb: 2,
                    textAlign: { xs: 'left', sm: 'justify' },
                    hyphens: 'auto'
                  }}
                >
                  Местоположението на читалищата е проверявано и ръчно, но този процес продължава и е възможно да има неточности. Приемаме информацията от официалния регистър, но реалните адреси могат да се различават.
                </Typography>
              </Box>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Box sx={{ 
                  bgcolor: 'rgba(255, 255, 255, 0.9)', 
                  p: { xs: 2, md: 3 }, 
                  borderRadius: 2,
                  height: '100%'
                }}>
                  <Typography 
                    variant="subtitle1"
                    sx={{ 
                      fontWeight: 600,
                      color: '#d32f2f',
                      mb: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: { xs: 'center', md: 'flex-start' }
                    }}
                  >
                    <Error sx={{ mr: 1, fontSize: 20 }} />
                    Възможни неточности
                  </Typography>
                  <List dense sx={{ mb: 0 }}>
                    {[
                      "Неточно географско местоположение",
                      "Променени адреси на читалища",
                      "Непълна информация в регистъра",
                      "Съвпадащи имена на населени места"
                    ].map((item, idx) => (
                      <ListItem key={idx} sx={{ px: 0, py: 0.5, alignItems: 'flex-start' }}>
                        <ListItemIcon sx={{ minWidth: 32, mt: 0.25 }}>
                          <Error sx={{ color: '#d32f2f', fontSize: 16 }} />
                        </ListItemIcon>
                        <ListItemText 
                          primary={item}
                          primaryTypographyProps={{ 
                            variant: 'body2',
                            textAlign: 'left'
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Box sx={{ 
                  bgcolor: 'rgba(255, 255, 255, 0.9)', 
                  p: { xs: 2, md: 3 }, 
                  borderRadius: 2,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column'
                }}>
                  <Typography 
                    variant="subtitle1"
                    sx={{ 
                      fontWeight: 600,
                      color: '#2e7d32',
                      mb: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: { xs: 'center', md: 'flex-start' }
                    }}
                  >
                    <CheckCircle sx={{ mr: 1, fontSize: 20 }} />
                    Как можем да помогнем
                  </Typography>
                  <Typography variant="body2" sx={{ 
                    mb: 3,
                    flexGrow: 1,
                    textAlign: { xs: 'center', md: 'justify' },
                    hyphens: 'auto'
                  }}>
                    Ако забележите неточен адрес или други грешни данни, можете да ни сигнализирате чрез формата за обратна връзка. Вашето съдействие ще помогне за подобряване на картата.
                  </Typography>
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: { xs: 'center', md: 'flex-start' },
                    mt: 'auto'
                  }}>
                    <Button
                      component={Link}
                      to="/feedback"
                      variant="contained"
                      startIcon={<Feedback />}
                      size={isMobile ? "small" : "medium"}
                      sx={{
                        bgcolor: '#2e7d32',
                        '&:hover': { bgcolor: '#1b5e20' },
                        borderRadius: 2
                      }}
                    >
                      Сигнализирай за неточност
                    </Button>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Бутони за навигация */}
      <Box sx={{ 
  display: 'flex', 
  justifyContent: 'center', 
  mt: { xs: 4, md: 6 }, 
  gap: { xs: 2, md: 3 }, 
  flexWrap: 'wrap',
  alignItems: 'center'
}}>
  <HomeRefreshButton 
    label="Към интерактивната карта"
    icon={<Map />}
    centered={false}
    size={isMobile ? "medium" : "large"}
    variant="contained"
  />

  <HomeRefreshButton 
    label="За проекта"
    icon={<Public />}
    to="/about"
    centered={false}
    size={isMobile ? "medium" : "large"}
    variant="outlined"
    color="secondary"
    fullWidth={false}
  />

  <HomeRefreshButton 
    label="Обратна връзка"
    icon={<Mail />}
    to="/feedback"
    centered={false}
    size={isMobile ? "medium" : "large"}
    variant="outlined"
    color="warning"
    fullWidth={false}
  />
</Box>

        {/* Допълнителна информация */}
        <Box sx={{ 
          mt: 6, 
          p: 3, 
          bgcolor: 'grey.50', 
          borderRadius: 3,
          textAlign: 'center'
        }}>
          <Typography 
            variant="body2" 
            sx={{ 
              color: 'text.secondary',
              fontStyle: 'italic',
              lineHeight: 1.6
            }}
          >
            Картата на народните читалища в България е проект с отворен код, чиято цел е да направи информацията за културните институции по-достъпна и прозрачна.
          </Typography>
        </Box>
      </motion.div>
    </Container>
  );
};

export default Methodology;